import type { StructureEdge, StructureNode } from '@tf/shared';
import { isConnectionStepHidden } from '@/components/application/isConnectionStepHidden';

interface AccountStructureGraph {
	nodes: StructureNode[];
	edges: StructureEdge[];
}

interface MainReview {
	graphNodeId: number;
	connectionKind: string;
	listEntities: { graphNodeId: number; entityKind: string }[];
}

interface ApplicantContext {
	filters: readonly string[] | undefined;
	applicantKind: string | undefined;
	applicantId: number | undefined;
	applicantConnectionKind: string;
	accountId: number;
}

export const getApplicantContext = (
	mainReview: MainReview,
	filters: readonly string[] | undefined,
	accountId: number,
): ApplicantContext => ({
	filters,
	applicantKind: mainReview.listEntities.find((entity) => entity.graphNodeId === mainReview.graphNodeId)?.entityKind,
	applicantId: mainReview.graphNodeId,
	applicantConnectionKind: mainReview.connectionKind,
	accountId,
});

export const getApplicantKindFilters = (filters: readonly string[] | undefined) =>
	filters?.filter((filter) => filter.includes(':')) ?? [];

export const isApplicantStructureHidden = (
	filters: readonly string[] | undefined,
	applicantConnectionKind: string,
	applicantKind: string | undefined,
) => isConnectionStepHidden(getApplicantKindFilters(filters), applicantConnectionKind, applicantKind);

export const hideApplicantKindConnections = (
	structure: AccountStructureGraph,
	{ filters, applicantKind, applicantId, applicantConnectionKind, accountId }: ApplicantContext,
): AccountStructureGraph => {
	const applicantKindFilters = getApplicantKindFilters(filters);
	if (!applicantKindFilters.length) {
		return structure;
	}

	const isApplicantHidden = isApplicantStructureHidden(applicantKindFilters, applicantConnectionKind, applicantKind);
	const isHidden = (connectionKind: string, parentEntityId: number | null) => {
		if (parentEntityId === accountId) {
			return false;
		}
		if (isApplicantHidden && parentEntityId === applicantId) {
			return true;
		}
		return isConnectionStepHidden(applicantKindFilters, connectionKind, applicantKind);
	};

	const visibleEdges = structure.edges.filter(
		(edge) => !(edge.data && isHidden(edge.data.meta.connectionKind, edge.data.parentEntityId)),
	);
	const nodeIds = new Set(
		structure.nodes
			.filter((node) => node.type === 'account' || !isHidden(node.data.meta.connectionKind, node.data.parentEntityId))
			.map((node) => node.id),
	);
	let hasNewNodes = true;
	while (hasNewNodes) {
		hasNewNodes = false;
		for (const edge of visibleEdges) {
			if (nodeIds.has(edge.source) && !nodeIds.has(edge.target)) {
				nodeIds.add(edge.target);
				hasNewNodes = true;
			}
		}
	}

	return {
		nodes: structure.nodes.filter((node) => nodeIds.has(node.id)),
		edges: visibleEdges.filter((edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target)),
	};
};
